'use client';

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  AppBar,
  Toolbar,
  Avatar,
  useTheme,
  Card,
  CardContent,
  Chip,
  IconButton,
  Drawer,
} from '@mui/material';
import {
  Home as HomeIcon,
  Business as BusinessIcon,
  ShoppingBag as ShoppingIcon,
  Work as JobIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingIcon,
  Security as SecurityIcon,
  Public as GlobalIcon,
  Language as LanguageIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { CompactDarkModeToggle } from '@/components/ui/DarkModeToggle';
import { Logo } from '@/components/ui/Logo';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { getUiCopy } from '@/i18n/uiCopy';

const productMeta = [
  {
    icon: BusinessIcon,
    href: '/listings',
    color: 'primary' as const,
  },
  {
    icon: HomeIcon,
    href: '/listings?type=property',
    color: 'secondary' as const,
  },
  {
    icon: ShoppingIcon,
    href: '/listings',
    color: 'info' as const,
  },
  {
    icon: JobIcon,
    href: '/listings?category=job',
    color: 'primary' as const,
  },
];

const featureMeta = [
  {
    icon: LanguageIcon,
    color: 'primary' as const,
  },
  {
    icon: GlobalIcon,
    color: 'info' as const,
  },
  {
    icon: SecurityIcon,
    color: 'success' as const,
  },
  {
    icon: TrendingIcon,
    color: 'secondary' as const,
  },
];

export default function HomePage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const locale = useLocale();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = useTranslations('nav');
  const copy = getUiCopy(locale);
  const products = productMeta.map((product, index) => ({
    ...product,
    ...copy.home.products[index],
  }));
  const features = featureMeta.map((feature, index) => ({
    ...feature,
    ...copy.home.features[index],
  }));

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: isDark ? 'rgba(10, 14, 26, 0.8)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg" className="px-3 sm:px-4 lg:px-6">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 }, py: { xs: 0.5, sm: 1 } }}>
            {/* Logo */}
            <Box className="flex items-center flex-grow">
              <Logo size="small" showText={true} href="/" />
            </Box>

            {/* Desktop Navigation - sm and up */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              <LanguageSwitcher variant="icon" />
              <CompactDarkModeToggle />
              <Button
                href="/auth/login"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 2,
                  py: 0.75,
                }}
              >
                {t('signIn')}
              </Button>
              <Button
                href="/auth/login"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 2,
                }}
              >
                {t('signUp')}
              </Button>
            </Stack>

            {/* Mobile Navigation - xs only */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ display: { xs: 'flex', sm: 'none' } }}
            >
              <LanguageSwitcher variant="icon" />
              <CompactDarkModeToggle />
              <IconButton
                edge="end"
                color="inherit"
                aria-label={locale === 'am' ? 'ምናሌ' : 'menu'}
                onClick={toggleMobileMenu}
                sx={{ p: 0.75, color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer Menu - Only auth buttons (no theme toggle duplicate) */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: { xs: 240, sm: 280 },
            bgcolor: isDark ? 'rgba(10, 14, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          {/* Header */}
          <Box className="flex items-center justify-end mb-6">
            <IconButton onClick={toggleMobileMenu} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Auth Buttons - only visible in drawer on mobile */}
          <Stack spacing={2}>
            <Button
              href="/auth/login"
              fullWidth
              onClick={toggleMobileMenu}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.938rem',
                py: 1.25,
                borderRadius: 2,
                justifyContent: 'center',
              }}
            >
              {t('signIn')}
            </Button>
            <Button
              href="/auth/login"
              variant="contained"
              fullWidth
              onClick={toggleMobileMenu}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.938rem',
                py: 1.25,
                borderRadius: 2,
              }}
            >
              {t('signUp')}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Hero Section */}
      <Box
        component="section"
        className="relative overflow-hidden"
        sx={{
          pt: { xs: 8, sm: 10, md: 12 },
          pb: { xs: 6, sm: 8, md: 10 },
        }}
      >
        {/* Animated Gradient Background */}
        <Box className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Primary Gradient Orb */}
          <Box
            className="absolute animate-pulse"
            sx={{
              top: { xs: '-20%', md: '-10%' },
              right: { xs: '-10%', md: '10%' },
              width: { xs: '280px', sm: '400px', md: '600px', lg: '700px' },
              height: { xs: '280px', sm: '400px', md: '600px', lg: '700px' },
              background: `radial-gradient(circle, ${theme.palette.primary.main}, transparent 70%)`,
              filter: 'blur(80px)',
              opacity: isDark ? 0.15 : 0.08,
              animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            }}
          />

          {/* Secondary Gradient Orb */}
          <Box
            className="absolute animate-pulse"
            sx={{
              bottom: { xs: '-20%', md: '-10%' },
              left: { xs: '-10%', md: '5%' },
              width: { xs: '240px', sm: '350px', md: '500px', lg: '600px' },
              height: { xs: '240px', sm: '350px', md: '500px', lg: '600px' },
              background: `radial-gradient(circle, ${theme.palette.secondary.main}, transparent 70%)`,
              filter: 'blur(80px)',
              opacity: isDark ? 0.15 : 0.08,
              animation: 'pulse 5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              animationDelay: '1s',
            }}
          />

          {/* Accent Gradient Orb */}
          <Box
            className="absolute hidden lg:block animate-pulse"
            sx={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '450px',
              height: '450px',
              background: `radial-gradient(circle, ${theme.palette.warning.main}, transparent 70%)`,
              filter: 'blur(100px)',
              opacity: isDark ? 0.1 : 0.05,
              animation: 'pulse 6s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              animationDelay: '2s',
            }}
          />
        </Box>

        <Container maxWidth="lg" className="relative z-10 px-3 sm:px-4 lg:px-6">
          <Box className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <Box className="animate-slide-down mb-4 sm:mb-6">
              <Chip
                icon={<span className="text-lg"></span>}
                label={copy.home.badge}
                sx={{
                  px: 2,
                  py: 2.5,
                  height: 'auto',
                  fontSize: { xs: '0.75rem', sm: '0.813rem' },
                  bgcolor: isDark ? 'rgba(167, 139, 250, 0.1)' : 'rgba(37, 99, 235, 0.1)',
                  border: 1,
                  borderColor: isDark ? 'rgba(167, 139, 250, 0.3)' : 'primary.main',
                  '& .MuiChip-label': {
                    px: 1,
                    color: isDark ? 'primary.light' : 'primary.main',
                    fontWeight: 700,
                  },
                }}
              />
            </Box>

            {/* Heading */}
            <Typography
              variant="h1"
              className="animate-slide-up text-balance mb-4 sm:mb-6"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
                fontWeight: 900,
                lineHeight: { xs: 1.15, sm: 1.1 },
                letterSpacing: '-0.03em',
                color: 'text.primary',
              }}
            >
              {copy.home.heroTitleStart}{' '}
              <Box
                component="span"
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #a78bfa 0%, #4ade80 50%, #a78bfa 100%)'
                    : 'linear-gradient(135deg, #2563eb 0%, #16a34a 50%, #2563eb 100%)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'gradient-shift 3s ease infinite',
                }}
              >
                {copy.home.heroTitleHighlight}
              </Box>
            </Typography>

            {/* Subheading */}
            <Typography
              className="animate-slide-up text-balance mb-6 sm:mb-8"
              sx={{
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                lineHeight: 1.6,
                color: 'text.secondary',
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
                px: { xs: 1, sm: 0 },
              }}
            >
              {copy.home.heroSubtitleStart}{' '}
              <Box component="span" sx={{ fontWeight: 700, color: isDark ? 'primary.light' : 'primary.main' }}>
                {copy.home.heroSubtitleEmphasis}
              </Box>{' '}
              {copy.home.heroSubtitleEnd}
            </Typography>

            {/* CTA Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 3 }}
              className="animate-scale-in justify-center"
              sx={{ px: { xs: 2, sm: 0 } }}
            >
              <Button
                href="/auth/login"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                className="shadow-xl hover:shadow-2xl transition-smooth hover-lift group"
                sx={{
                  px: { xs: 4, sm: 5 },
                  py: { xs: 1.75, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  '& .MuiButton-endIcon': {
                    transition: 'transform 0.2s',
                  },
                  '&:hover .MuiButton-endIcon': {
                    transform: 'translateX(4px)',
                  },
                }}
              >
                {copy.home.primaryCta}
              </Button>
              <Button
                href="#products"
                variant="outlined"
                size="large"
                className="border-2 hover-lift transition-smooth"
                sx={{
                  px: { xs: 4, sm: 5 },
                  py: { xs: 1.75, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  borderWidth: 2,
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 1)',
                  },
                }}
              >
                {copy.home.secondaryCta}
              </Button>
            </Stack>

            {/* Social Proof */}
            <Box className="mt-8 sm:mt-10 animate-fade-in">
              <Typography
                variant="caption"
                className="block mb-4"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.813rem' },
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontWeight: 600,
                }}
              >
                {copy.home.socialProof}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                className="justify-center flex-wrap"
                sx={{ gap: 1 }}
              >
                {[...Array(5)].map((_, i) => (
                  <Avatar
                    key={i}
                    className="border-2 border-white dark:border-gray-900 hover-scale"
                    sx={{
                      width: { xs: 36, sm: 44 },
                      height: { xs: 36, sm: 44 },
                      bgcolor: `hsl(${i * 72}, 70%, 60%)`,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      boxShadow: 2,
                    }}
                  >
                    {String.fromCodePoint(0x1f9d1 + i)}
                  </Avatar>
                ))}
                <Avatar
                  className="border-2 border-white dark:border-gray-900 font-bold"
                  sx={{
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    bgcolor: 'primary.main',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    boxShadow: 2,
                  }}
                >
                  +2K
                </Avatar>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Products Section */}
      <Box
        id="products"
        component="section"
        className="relative scroll-mt-16"
        sx={{
          py: { xs: 6, sm: 8, md: 10 },
          bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(249, 250, 251, 0.5)',
        }}
      >
        <Container maxWidth="lg" className="px-3 sm:px-4 lg:px-6">
          {/* Section Header */}
          <Box className="mb-8 sm:mb-10 text-center max-w-3xl mx-auto">
            <Typography
              variant="overline"
              className="block mb-3 font-bold tracking-wider"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.813rem' },
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {copy.home.productsEyebrow}
            </Typography>
            <Typography
              variant="h2"
              className="mb-4 text-balance"
              sx={{
                fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: 'text.primary',
              }}
            >
              {copy.home.productsTitleStart}{' '}
              <Box
                component="span"
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #a78bfa, #4ade80)'
                    : 'linear-gradient(135deg, #2563eb, #16a34a)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {copy.home.productsTitleHighlight}
              </Box>
            </Typography>
            <Typography
              className="text-balance"
              sx={{
                fontSize: { xs: '1.063rem', sm: '1.125rem', md: '1.25rem' },
                color: 'text.secondary',
                lineHeight: 1.7,
              }}
            >
              {copy.home.productsSubtitle}
            </Typography>
          </Box>

          {/* Product Grid */}
          <Box
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6"
            sx={{
              gridAutoRows: '1fr',
            }}
          >
            {products.map((product, index) => {
              const Icon = product.icon;
              return (
                <Box key={product.title}>
                  <Card
                    component={Link}
                    href={product.href}
                    className="h-full card-interactive group overflow-hidden"
                    sx={{
                      textDecoration: 'none',
                      bgcolor: isDark ? 'rgba(20, 24, 36, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(12px)',
                      border: 1,
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: isDark
                          ? '0 20px 40px rgba(0, 0, 0, 0.6)'
                          : '0 20px 40px rgba(0, 0, 0, 0.12)',
                        borderColor: `${product.color}.main`,
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        bgcolor: `${product.color}.main`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      },
                      '&:hover::before': {
                        transform: 'scaleX(1)',
                      },
                    }}
                  >
                    <CardContent
                      className="relative"
                      sx={{ p: { xs: 3, sm: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                      {/* Icon */}
                      <Box
                        className="mb-5 transition-transform group-hover:scale-110 group-hover:rotate-3"
                        sx={{
                          width: { xs: 60, sm: 68 },
                          height: { xs: 60, sm: 68 },
                          borderRadius: 3,
                          bgcolor: `${product.color}.main`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: isDark
                            ? `0 8px 24px ${theme.palette[product.color].dark}40`
                            : `0 8px 24px ${theme.palette[product.color].main}30`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <Icon sx={{ fontSize: { xs: 30, sm: 34 }, color: 'white' }} />
                      </Box>

                      {/* Content */}
                      <Typography
                        variant="h5"
                        className="mb-3"
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          color: 'text.primary',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {product.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          fontSize: { xs: '0.938rem', sm: '1rem' },
                        }}
                      >
                        {product.description}
                      </Typography>

                      {/* Arrow Icon - Explore */}
                      <Box
                        className="flex items-center gap-1.5 text-primary-600 dark:text-primary-400 font-semibold"
                        sx={{
                          mt: 1,
                          transition: 'all 0.2s',
                          '& svg': {
                            transition: 'transform 0.2s',
                          },
                          '.group:hover &': {
                            '& svg': {
                              transform: 'translateX(4px)',
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                        >
                          {copy.home.explore}
                        </Typography>
                        <ArrowForwardIcon sx={{ fontSize: 18 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        component="section"
        sx={{ py: { xs: 6, sm: 8, md: 10 } }}
      >
        <Container maxWidth="lg" className="px-3 sm:px-4 lg:px-6">
          {/* Section Header */}
          <Box className="mb-8 sm:mb-10 text-center max-w-2xl mx-auto">
            <Typography
              variant="overline"
              className="block mb-3 font-bold tracking-wider"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.813rem' },
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {copy.home.whyEyebrow}
            </Typography>
            <Typography
              variant="h2"
              className="text-balance"
              sx={{
                fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: 'text.primary',
              }}
            >
              {copy.home.whyTitleStart}{' '}
              <Box
                component="span"
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #4ade80, #a78bfa)'
                    : 'linear-gradient(135deg, #16a34a, #2563eb)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {copy.home.whyTitleHighlight}
              </Box>
            </Typography>
          </Box>

          {/* Features Grid */}
          <Box
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-10"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Box key={feature.title}>
                  <Box
                    className="text-center group"
                    sx={{
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                      },
                    }}
                  >
                    {/* Icon Container */}
                    <Box
                      className="mx-auto mb-5 relative"
                      sx={{
                        width: { xs: 72, sm: 80 },
                        height: { xs: 72, sm: 80 },
                        borderRadius: 4,
                        bgcolor: isDark
                          ? `rgba(${theme.palette[feature.color].main}, 0.1)`
                          : `rgba(${theme.palette[feature.color].main}, 0.08)`,
                        border: 2,
                        borderColor: isDark
                          ? `rgba(${theme.palette[feature.color].main}, 0.2)`
                          : `rgba(${theme.palette[feature.color].main}, 0.15)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: isDark
                          ? `0 8px 32px ${theme.palette[feature.color].dark}20`
                          : `0 8px 32px ${theme.palette[feature.color].main}15`,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: -2,
                          borderRadius: 4,
                          padding: 2,
                          background: `linear-gradient(135deg, ${theme.palette[feature.color].light}, ${theme.palette[feature.color].dark})`,
                          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          maskComposite: 'exclude',
                          WebkitMaskComposite: 'xor',
                          opacity: 0,
                          transition: 'opacity 0.3s',
                        },
                        '.group:hover &::before': {
                          opacity: 1,
                        },
                        '.group:hover &': {
                          transform: 'scale(1.1) rotate(5deg)',
                          bgcolor: `${feature.color}.main`,
                          borderColor: `${feature.color}.main`,
                        },
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: { xs: 36, sm: 40 },
                          color: `${feature.color}.main`,
                          transition: 'all 0.3s',
                          '.group:hover &': {
                            color: 'white',
                            transform: 'scale(1.1)',
                          },
                        }}
                      />
                    </Box>

                    {/* Content */}
                    <Typography
                      variant="h6"
                      className="mb-2"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.125rem', sm: '1.25rem' },
                        color: 'text.primary',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: { xs: '0.938rem', sm: '1rem' },
                        px: { xs: 2, md: 0 },
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        component="section"
        className="relative overflow-hidden"
        sx={{
          py: { xs: 6, sm: 8, md: 10 },
          bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(249, 250, 251, 0.5)',
        }}
      >
        {/* Background Gradient */}
        <Box
          className="absolute inset-0 pointer-events-none"
          sx={{
            background: isDark
              ? `radial-gradient(ellipse at center, ${theme.palette.primary.dark}15 0%, transparent 70%)`
              : `radial-gradient(ellipse at center, ${theme.palette.primary.main}08 0%, transparent 70%)`,
          }}
        />

        <Container maxWidth="md" className="relative z-10 px-3 sm:px-4 lg:px-6">
          <Box className="text-center">
            {/* Heading */}
            <Typography
              variant="h2"
              className="mb-5 text-balance"
              sx={{
                fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: 'text.primary',
              }}
            >
              {copy.home.finalTitleStart}{' '}
              <Box
                component="span"
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #a78bfa, #4ade80)'
                    : 'linear-gradient(135deg, #2563eb, #16a34a)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {copy.home.finalTitleHighlight}
              </Box>
            </Typography>

            {/* Subheading */}
            <Typography
              variant="h6"
              className="text-balance mb-8"
              sx={{
                fontSize: { xs: '1.063rem', sm: '1.25rem' },
                color: 'text.secondary',
                maxWidth: '550px',
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.7,
                px: { xs: 2, sm: 0 },
              }}
            >
              {copy.home.finalSubtitle}
            </Typography>

            {/* CTA Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 3 }}
              className="justify-center mb-8"
              sx={{ px: { xs: 2, sm: 0 } }}
            >
              <Button
                href="/auth/login"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                className="shadow-xl hover:shadow-2xl transition-smooth hover-lift"
                sx={{
                  px: { xs: 4, sm: 5 },
                  py: { xs: 1.75, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  '& .MuiButton-endIcon': {
                    transition: 'transform 0.2s',
                  },
                  '&:hover .MuiButton-endIcon': {
                    transform: 'translateX(4px)',
                  },
                }}
              >
                {copy.home.finalPrimaryCta}
              </Button>
              <Button
                href="/listings"
                variant="outlined"
                size="large"
                className="border-2 hover-lift transition-smooth"
                sx={{
                  px: { xs: 4, sm: 5 },
                  py: { xs: 1.75, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  borderWidth: 2,
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 1)',
                  },
                }}
              >
                {copy.home.finalSecondaryCta}
              </Button>
            </Stack>

            {/* Stats */}
            <Box className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-12">
              {copy.home.stats.map((stat) => (
                <Box key={stat.label}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: '1.75rem', sm: '2.5rem' },
                        fontWeight: 900,
                        mb: 0.5,
                        background: isDark
                          ? 'linear-gradient(135deg, #a78bfa, #4ade80)'
                          : 'linear-gradient(135deg, #2563eb, #16a34a)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: { xs: '0.813rem', sm: '0.938rem' },
                        fontWeight: 600,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        className="border-t border-gray-200 dark:border-gray-800"
        sx={{
          py: { xs: 4, sm: 6 },
          bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'background.default',
        }}
      >
        <Container maxWidth="lg" className="px-3 sm:px-4 lg:px-6">
          <Box className="text-center">
            {/* Logo */}
            <Box className="flex items-center justify-center mb-3">
              <Logo size="medium" showText={true} href="/" />
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              className="mb-3 text-balance"
              sx={{
                color: 'text.secondary',
                maxWidth: '500px',
                mx: 'auto',
                fontSize: { xs: '0.875rem', sm: '0.938rem' },
                lineHeight: 1.5,
              }}
            >
              {copy.home.footerDescription}
            </Typography>

            {/* Copyright */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.813rem' },
                display: 'block',
              }}
            >
              © {new Date().getFullYear()} Mengir. {locale === 'am' ? 'መብቶቹ ሁሉ የተጠበቁ ናቸው።' : 'All rights reserved.'}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
